/**
 * MenuButton - An atom component for dropdown menu trigger buttons.
 *
 * A button with icon, text, and chevron indicator.
 * Used as a trigger for Dropdown components.
 */

import { type ReactNode } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

export interface MenuButtonProps {
  /**
   * Optional icon to display before the text.
   */
  icon?: LucideIcon;
  /**
   * The button content (text).
   */
  children: ReactNode;
  /**
   * Whether the dropdown is open.
   */
  isOpen?: boolean;
  /**
   * Callback invoked when the button is clicked.
   */
  onClick?: () => void;
  /**
   * ARIA label for accessibility.
   */
  'aria-label'?: string;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

export function MenuButton({
  icon: Icon,
  children,
  isOpen = false,
  onClick,
  'aria-label': ariaLabel,
  className,
}: MenuButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 text-sm',
        `
          border border-border-default bg-bg-elevated
          dark:border-border-default dark:bg-bg-elevated
        `,
        `
          rounded-md
          hover:bg-bg-base
          dark:hover:bg-bg-subtle
        `,
        'focus:ring-2 focus:ring-primary-500 focus:outline-hidden',
        `
          text-text-primary transition-colors
          dark:text-text-primary
        `,
        className,
      )}
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      aria-haspopup="true"
    >
      {Icon && <Icon className="size-4 shrink-0" />}
      <span
        className="
          hidden max-w-30 truncate
          sm:inline
        "
      >
        {children}
      </span>
      <ChevronDown className={cn(`size-4 shrink-0 text-text-muted transition-transform`, isOpen && `rotate-180`)} />
    </button>
  );
}
